// TaskHubSettingsService.ts

import {
  TaskHubUserSettings,
  TaskHubQuery,
  TaskHubFilters,
} from './TaskHubSettings.types';
import {
  apiFetchTaskHubUserSettings,
  apiSaveTaskHubUserSettings,
  createTaskHubQuery,
} from './TaskHubSettings.api';

export class TaskHubSettingsService {
  private settings: TaskHubUserSettings | null = null;
  private settingId: string | null = null;

  async loadSettings(contactId: string): Promise<TaskHubUserSettings> {
    const result = await apiFetchTaskHubUserSettings(contactId);
    this.settings = result.settings;
    this.settingId = result.settingId || null;
    return this.settings;
  }

  async saveSettings(
    contactId: string,
    settings: TaskHubUserSettings
  ): Promise<void> {
    // check if default query is set otherwise set empty guid
    if (!settings.defaultTaskHubId) {
      settings.defaultTaskHubId = '00000000-0000-0000-0000-000000000000';
    }
    await apiSaveTaskHubUserSettings(
      contactId,
      settings,
      this.settingId || undefined
    );
    this.settings = settings;
  }

  getQueries(): TaskHubQuery[] {
    return this.settings?.taskHubQueries || [];
  }

  getDefaultQuery(): TaskHubQuery | null {
    if (!this.settings?.defaultTaskHubId) {
      return null;
    }

    return (
      this.getQueries().find(
        (q) => q.taskHubQueryId === this.settings!.defaultTaskHubId
      ) || null
    );
  }

  findQuery(queryId: string): TaskHubQuery | null {
    return this.getQueries().find((q) => q.taskHubQueryId === queryId) || null;
  }

  // FIXED: Return the created query
  async addQuery(
    contactId: string,
    displayName: string,
    filters: TaskHubFilters
  ): Promise<TaskHubQuery> {
    if (!this.settings) {
      throw new Error('Settings not loaded');
    }

    const newQuery = createTaskHubQuery(displayName, filters);
    const updatedSettings: TaskHubUserSettings = {
      ...this.settings,
      taskHubQueries: [...this.settings.taskHubQueries, newQuery],
    };

    await this.saveSettings(contactId, updatedSettings);
    return newQuery; // Return the created query
  }

  async deleteQuery(contactId: string, queryId: string): Promise<void> {
    if (!this.settings) {
      throw new Error('Settings not loaded');
    }

    const updatedQueries = this.settings.taskHubQueries.filter(
      (q) => q.taskHubQueryId !== queryId
    );

    let updatedDefaultId = this.settings.defaultTaskHubId;

    // If the default query is being deleted, set the first remaining query as default
    if (
      this.settings.defaultTaskHubId === queryId &&
      updatedQueries.length > 0
    ) {
      updatedDefaultId = updatedQueries[0].taskHubQueryId;
    } else if (updatedQueries.length === 0) {
      updatedDefaultId = '';
    }

    const updatedSettings: TaskHubUserSettings = {
      ...this.settings,
      taskHubQueries: updatedQueries,
      defaultTaskHubId: updatedDefaultId,
    };

    await this.saveSettings(contactId, updatedSettings);
  }

  async setDefaultQuery(contactId: string, queryId: string): Promise<void> {
    if (!this.settings) {
      throw new Error('Settings not loaded');
    }

    const queryExists = this.settings.taskHubQueries.some(
      (q) => q.taskHubQueryId === queryId
    );

    if (!queryExists) {
      throw new Error('Query not found');
    }

    const updatedSettings: TaskHubUserSettings = {
      ...this.settings,
      defaultTaskHubId: queryId,
    };

    await this.saveSettings(contactId, updatedSettings);
  }

  // FIXED: Add method to update existing query
  async updateQuery(
    contactId: string,
    queryId: string,
    displayName: string,
    filters: TaskHubFilters
  ): Promise<TaskHubQuery> {
    if (!this.settings) {
      throw new Error('Settings not loaded');
    }

    const queryIndex = this.settings.taskHubQueries.findIndex(
      (q) => q.taskHubQueryId === queryId
    );

    if (queryIndex === -1) {
      throw new Error('Query not found');
    }

    const updatedQuery = createTaskHubQuery(displayName, filters);
    // Keep the same ID for consistency
    updatedQuery.taskHubQueryId = queryId;

    const updatedQueries = [...this.settings.taskHubQueries];
    updatedQueries[queryIndex] = updatedQuery;

    const updatedSettings: TaskHubUserSettings = {
      ...this.settings,
      taskHubQueries: updatedQueries,
    };

    await this.saveSettings(contactId, updatedSettings);
    return updatedQuery;
  }
}
